import {
  ExplainerActions,
  ExplainerCallout,
  ExplainerLayout,
  ExplainerList,
  ExplainerScript,
  ExplainerSection,
  TodoExplainerComponent,
} from "./todoExplainer"
import type { ExplainerVars } from "../../reduxToolkit/selectors/resolveTodoExplainer"

export type { ExplainerVars }

/**
 * Builds the explainer component for the given record/todo type, or returns
 * undefined if no explainer is registered. Falling through to undefined means
 * the existing direct-to-action behavior should be used.
 */
export function buildTodoExplainer(
  recordType: string,
  todoType: string,
  vars: ExplainerVars = {},
): TodoExplainerComponent | undefined {
  const builder = BUILDERS[`${recordType}/${todoType}`]
  return builder ? builder(vars) : undefined
}

const BUILDERS: Record<string, (vars: ExplainerVars) => TodoExplainerComponent> = {
  "expense/itemized-bill": ({ provider }) => ({ onProceed, onSecondary, onCancel }) => (
    <ExplainerLayout
      heading={provider ? `Get an itemized bill from ${provider}` : "Get an itemized bill"}
      subheading="An itemized bill — not a receipt — is required for sharing."
    >
      <ExplainerSection>
        <p>
          An itemized bill lists every charge with its CPT code (e.g. <code>99213</code>,{" "}
          <code>80053</code>). A receipt or EOB only shows the total, which isn't enough for
          your sharing plan to review.
        </p>
      </ExplainerSection>

      {onSecondary && (
        <ExplainerSection heading="Let Alice get it for you">
          <p>
            Alice, our AI assistant, can contact {provider ?? "the provider"} on your behalf to
            request the itemized bill — you don't have to do anything else.
          </p>
        </ExplainerSection>
      )}

      <ExplainerSection heading="Already have it?">
        <p>
          Upload it to your attachments and we'll automatically match it to this expense.
        </p>
      </ExplainerSection>

      <ExplainerCallout
        variant="warning"
        body={
          <span>
            Without an itemized bill, your sharing plan may delay or deny reimbursement.
          </span>
        }
      />

      <ExplainerActions
        primaryLabel="Upload itemized bill"
        onProceed={onProceed}
        secondaryLabel={onSecondary ? "Contact my provider" : undefined}
        onSecondary={onSecondary}
        onCancel={onCancel}
      />
    </ExplainerLayout>
  ),

  "incident/ninety-day-gap": ({ gapDays }) => ({ onProceed, onCancel }) => (
    <ExplainerLayout
      heading={gapDays ? `Split this incident at the ${gapDays}-day gap` : "Split this incident at the gap"}
      subheading="CHM treats expenses more than 90 days apart as separate incidents."
    >
      <ExplainerSection>
        <p>
          Each separate incident gets its own personal responsibility. Splitting won't change
          anything that's already been paid — it just lines up your records with how CHM
          tracks things.
        </p>
      </ExplainerSection>

      <ExplainerSection heading="What happens next">
        <ExplainerList
          variant="numbered"
          items={[
            "We'll show the gap on a timeline.",
            "You pick where to split.",
            "Each side becomes its own incident with its own submissions.",
          ]}
        />
      </ExplainerSection>

      <ExplainerCallout
        variant="info"
        body={<span>You can always merge the incidents back together later.</span>}
      />

      <ExplainerActions primaryLabel="Review the split" onProceed={onProceed} onCancel={onCancel} />
    </ExplainerLayout>
  ),

  "incident/maternity-prepayment-agreement": () => ({ onProceed, onCancel }) => (
    <ExplainerLayout
      heading="Save 30–50% with a hospital prepayment agreement"
      subheading="Most hospitals offer a discount if you pay up front."
    >
      <ExplainerSection>
        <p>
          Hospitals routinely give cash-pay or self-pay discounts that can cut a $20,000
          delivery to under $10,000. Your sharing plan still covers the discounted amount — so
          the savings are real money back in your pocket.
        </p>
      </ExplainerSection>

      <ExplainerSection heading="Script for the call">
        <ExplainerScript>
          "Hi, I'm a self-pay patient delivering at your hospital. What discount do you offer
          for cash-pay or prepayment, and can you send me a written estimate?"
        </ExplainerScript>
      </ExplainerSection>

      <ExplainerSection heading="What the agreement should include">
        <ExplainerList
          variant="checklist"
          items={[
            "Estimated total cost for delivery",
            "Discount percentage or final cash-pay amount",
            "Payment due date(s)",
            "What's included (hospital fees, doctor fees, anesthesia)",
          ]}
        />
      </ExplainerSection>

      <ExplainerCallout
        variant="success"
        body={
          <span>
            Once you have the PDF, upload it and we'll attach it to your CHM submission.
          </span>
        }
      />

      <ExplainerActions
        primaryLabel="I have the agreement"
        onProceed={onProceed}
        cancelLabel="I'll get this later"
        onCancel={onCancel}
      />
    </ExplainerLayout>
  ),

  "submission/personal-responsibility": () => ({ onProceed, onCancel }) => (
    <ExplainerLayout
      heading="Get your CHM personal responsibility reimbursed"
      subheading="This is money your HRA can pay back to you — you don't owe CHM anything."
    >
      <ExplainerSection>
        <p>
          Each year, CHM doesn't share the first part of your household's medical costs — your{" "}
          <strong>personal responsibility</strong>. CHM keeps that amount out of your
          reimbursement, sends you a check for the rest, and mails you a letter stating how much
          they withheld.
        </p>
      </ExplainerSection>

      <ExplainerSection heading="Why you're seeing this">
        <p>
          Your employer's HRA can reimburse the amount CHM withheld. This task walks you through
          submitting that CHM letter to your HRA so the money comes back to you.
        </p>
      </ExplainerSection>

      <ExplainerCallout
        variant="info"
        body={
          <span>
            CHM mails you that letter after they process your submission. If it hasn't arrived
            yet, come back to this task once it does.
          </span>
        }
      />

      <ExplainerSection heading="What you'll need">
        <ExplainerList
          variant="checklist"
          items={["The letter from CHM showing your personal responsibility amount"]}
        />
      </ExplainerSection>

      <ExplainerActions
        primaryLabel="Submit my CHM letter"
        onProceed={onProceed}
        cancelLabel="I'll do this later"
        onCancel={onCancel}
      />
    </ExplainerLayout>
  ),

  "expense/prepayment-agreement-pdf": ({ provider }) => ({ onProceed, onCancel }) => (
    <ExplainerLayout
      heading={provider ? `Upload your prepayment agreement from ${provider}` : "Upload your prepayment agreement"}
      subheading="This is the discount contract you signed before the procedure."
    >
      <ExplainerSection>
        <p>
          A prepayment agreement is the document where the provider agreed to a cash-pay
          discount. It's usually 1–2 pages and shows the discounted price and payment terms.
        </p>
      </ExplainerSection>

      <ExplainerSection heading="Where to look">
        <ExplainerList
          variant="checklist"
          items={[
            "An email from the provider's billing office before the procedure",
            "A signed PDF in your patient portal",
            "A paper copy from intake — a phone scan works fine",
          ]}
        />
      </ExplainerSection>

      <ExplainerCallout
        variant="info"
        body={
          <span>
            No agreement on file? You can skip this — it's only required when you're claiming
            a cash-pay discount.
          </span>
        }
      />

      <ExplainerActions primaryLabel="Upload agreement" onProceed={onProceed} onCancel={onCancel} />
    </ExplainerLayout>
  ),
}
